import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveCommunityGrading,
  approveCommunityMembership,
  approveCommunityPosition,
  CommunityMemberAdmin,
  fetchCommunityMembers,
  MembershipFilter,
  QuestionnaireFilter,
} from '../../../../services/adminCommunityService';
import {
  formatReviewCountdown,
  reviewTimeRemaining,
} from '../../../Community/marketProfile/marketProfileStore';
import { formatRub, positionFixValue, positionStiValue, type PositionFormData } from '../../../Community/marketProfile/positionFormModel';
import {
  AdminEmpty,
  AdminPageHeader,
  AdminTableWrap,
  btnPrimary,
  btnSecondary,
  filtersRowClass,
  inputClass,
  selectClass,
  tdClass,
  thClass,
} from '../adminUi';
import { AdminModal, ConfirmDialog } from '../adminShared';
import GradingAnswersReview from './GradingAnswersReview';

const MEMBERSHIP_OPTIONS: { value: MembershipFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'approved', label: 'Участники' },
  { value: 'not_approved', label: 'Не одобрены' },
];

const QUESTIONNAIRE_OPTIONS: { value: QuestionnaireFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'yes', label: 'Да' },
  { value: 'no', label: 'Нет' },
  { value: 'pending', label: 'На проверке' },
];

function membershipLabel(status: CommunityMemberAdmin['membershipStatus']): string {
  if (status === 'approved') return 'Участник';
  if (status === 'pending' || status === 'none') return 'Не одобрен';
  if (status === 'rejected') return 'Отклонён';
  return '—';
}

function questionnaireLabel(status: string): string {
  if (status === 'complete') return 'Да';
  if (status === 'pending_review') return 'На проверке';
  return 'Нет';
}

function ReviewTimer({ submittedAt }: { submittedAt: string | null }) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!submittedAt) return undefined;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [submittedAt]);

  const remaining = reviewTimeRemaining(submittedAt);
  if (remaining == null) return null;
  const overdue = remaining <= 0;
  return (
    <span className={`text-xs ${overdue ? 'text-amber-700' : 'text-slate-500'}`}>
      {overdue ? 'Срок проверки истёк' : formatReviewCountdown(remaining)}
    </span>
  );
}

function MemberDetailDrawer({
  member,
  onClose,
  onApproveMembership,
  onApprovePosition,
  onApproveGrading,
  busy,
}: {
  member: CommunityMemberAdmin;
  onClose: () => void;
  onApproveMembership: () => void;
  onApprovePosition: () => void;
  onApproveGrading: () => void;
  busy: boolean;
}) {
  const profile = member.profileState;
  const position = profile?.position?.data;
  const gradingState = profile?.grading;
  const gradingResult = gradingState?.result ?? null;
  const gradingAnswers = gradingState?.answers ?? [];
  const hasGradingData =
    Boolean(gradingResult)
    || gradingAnswers.length > 0
    || (gradingState?.status && gradingState.status !== 'empty');

  return (
    <AdminModal title="Карточка участника" onClose={onClose} wide>
      <div className="space-y-4 text-sm text-slate-700">
        <div className="grid gap-3 sm:grid-cols-2">
          <div><span className="text-slate-500">Email:</span> {member.email || '—'}</div>
          <div><span className="text-slate-500">Телефон:</span> {member.phone || '—'}</div>
          <div><span className="text-slate-500">Имя:</span> {[member.firstName, member.lastName].filter(Boolean).join(' ') || '—'}</div>
          <div><span className="text-slate-500">Псевдоним:</span> {member.pseudonym || '—'}</div>
          <div><span className="text-slate-500">Компания:</span> {member.company || '—'}</div>
          <div><span className="text-slate-500">Сектор:</span> {member.sector || '—'}</div>
          <div><span className="text-slate-500">Должность:</span> {member.title || '—'}</div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="font-medium text-slate-900">Статус в сообществе</div>
          <div className="mt-1">{membershipLabel(member.membershipStatus)}</div>
          {member.membershipStatus !== 'approved' && (
            <button type="button" className={`${btnPrimary} mt-3`} disabled={busy} onClick={onApproveMembership}>
              Одобрить участника
            </button>
          )}
        </div>

        {position && (
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="font-medium text-slate-900">Данные о должности</div>
            <div className="mt-2 grid gap-1 sm:grid-cols-2">
              <div>Индустрия: {position.industryName || '—'}</div>
              <div>Сектор: {position.sectorName || '—'}</div>
              <div>Компания: {position.companyName || '—'}</div>
              <div>Роль: {position.companyRoleName || '—'}</div>
              <div>FIX: {positionFixValue(position as PositionFormData) ? formatRub(positionFixValue(position as PositionFormData)) : '—'}</div>
              <div>STI: {positionStiValue(position as PositionFormData) ? `${positionStiValue(position as PositionFormData)}%` : '—'}</div>
            </div>
            {member.positionStatus === 'pending_review' && (
              <button type="button" className={`${btnPrimary} mt-3`} disabled={busy} onClick={onApprovePosition}>
                Подтвердить данные о должности
              </button>
            )}
          </div>
        )}

        {hasGradingData && (
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="font-medium text-slate-900">Анкета грейдинга</div>
            <div className="mt-3">
              <GradingAnswersReview result={gradingResult} answers={gradingAnswers} />
            </div>
            {member.gradingStatus === 'pending_review' && (
              <button type="button" className={`${btnPrimary} mt-3`} disabled={busy} onClick={onApproveGrading}>
                Подтвердить грейд
              </button>
            )}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

export default function CommunityAdminSection() {
  const [members, setMembers] = useState<CommunityMemberAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<CommunityMemberAdmin | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    action: () => Promise<void>;
  } | null>(null);

  const [membershipFilter, setMembershipFilter] = useState<MembershipFilter>('all');
  const [positionFilter, setPositionFilter] = useState<QuestionnaireFilter>('all');
  const [gradingFilter, setGradingFilter] = useState<QuestionnaireFilter>('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await fetchCommunityMembers({
        membership: membershipFilter,
        position: positionFilter,
        grading: gradingFilter,
        search,
      });
      setMembers(response.data);
      setSelected((current) => (
        current ? response.data.find((m) => m.id === current.id) ?? null : null
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      setMembers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [membershipFilter, positionFilter, gradingFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCounts = useMemo(() => {
    return {
      membership: members.filter((m) => m.membershipStatus !== 'approved').length,
      position: members.filter((m) => m.positionStatus === 'pending_review').length,
      grading: members.filter((m) => m.gradingStatus === 'pending_review').length,
    };
  }, [members]);

  const runAction = async (memberId: string, action: () => Promise<void>) => {
    setBusyId(memberId);
    try {
      await action();
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка операции');
    } finally {
      setBusyId(null);
      setConfirmAction(null);
    }
  };

  if (loading) return <AdminEmpty>Загрузка…</AdminEmpty>;

  return (
    <>
      <AdminPageHeader
        title="Сообщество"
        subtitle="Участники, вступительные заявки и проверка анкет профиля рынка"
        actions={
          <button type="button" className={btnSecondary} disabled={refreshing} onClick={() => load(true)}>
            {refreshing ? 'Обновление…' : 'Обновить'}
          </button>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className={`${filtersRowClass} mb-4`}>
        <input
          type="search"
          className={inputClass}
          placeholder="Поиск по имени, email, компании…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className={selectClass} value={membershipFilter} onChange={(e) => setMembershipFilter(e.target.value as MembershipFilter)}>
          {MEMBERSHIP_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}{o.value === 'not_approved' && pendingCounts.membership ? ` (${pendingCounts.membership})` : ''}</option>
          ))}
        </select>
        <select className={selectClass} value={positionFilter} onChange={(e) => setPositionFilter(e.target.value as QuestionnaireFilter)}>
          {QUESTIONNAIRE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>Должность: {o.label}{o.value === 'pending' && pendingCounts.position ? ` (${pendingCounts.position})` : ''}</option>
          ))}
        </select>
        <select className={selectClass} value={gradingFilter} onChange={(e) => setGradingFilter(e.target.value as QuestionnaireFilter)}>
          {QUESTIONNAIRE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>Грейдинг: {o.label}{o.value === 'pending' && pendingCounts.grading ? ` (${pendingCounts.grading})` : ''}</option>
          ))}
        </select>
      </div>

      {members.length === 0 ? (
        <AdminEmpty>Участники не найдены</AdminEmpty>
      ) : (
        <AdminTableWrap>
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className={thClass}>Участник</th>
                <th className={thClass}>Email</th>
                <th className={thClass}>В сообществе</th>
                <th className={thClass}>Должность</th>
                <th className={thClass}>Грейдинг</th>
                <th className={thClass}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                  <tr key={member.id} className="border-t border-slate-100">
                    <td className={tdClass}>
                      <div className="font-medium text-slate-900">
                        {member.pseudonym || [member.firstName, member.lastName].filter(Boolean).join(' ') || '—'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {[member.company, member.sector, member.title].filter(Boolean).join(' · ') || ''}
                      </div>
                    </td>
                    <td className={tdClass}>{member.email || '—'}</td>
                    <td className={tdClass}>
                      <span className={member.membershipStatus === 'approved' ? 'text-emerald-700' : 'text-amber-700'}>
                        {membershipLabel(member.membershipStatus)}
                      </span>
                    </td>
                    <td className={tdClass}>
                      <div>{questionnaireLabel(member.positionStatus)}</div>
                      {member.positionStatus === 'pending_review' && (
                        <ReviewTimer submittedAt={member.positionReviewSubmittedAt} />
                      )}
                    </td>
                    <td className={tdClass}>
                      <div>{questionnaireLabel(member.gradingStatus)}</div>
                      {member.gradingStatus === 'pending_review' && (
                        <ReviewTimer submittedAt={member.gradingReviewSubmittedAt} />
                      )}
                    </td>
                    <td className={tdClass}>
                      <button type="button" className={btnSecondary} onClick={() => setSelected(member)}>
                        Подробнее
                      </button>
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        </AdminTableWrap>
      )}

      {selected && (
        <MemberDetailDrawer
          member={selected}
          onClose={() => setSelected(null)}
          busy={busyId === selected.id}
          onApproveMembership={() => setConfirmAction({
            title: 'Одобрить участника',
            message: `Пропустить ${selected.email} в сообщество?`,
            action: () => runAction(selected.id, () => approveCommunityMembership(selected.id)),
          })}
          onApprovePosition={() => setConfirmAction({
            title: 'Подтвердить данные о должности',
            message: `Подтвердить анкету должности для ${selected.email}?`,
            action: () => runAction(selected.id, () => approveCommunityPosition(selected.id)),
          })}
          onApproveGrading={() => setConfirmAction({
            title: 'Подтвердить грейд',
            message: `Подтвердить грейд для ${selected.email}?`,
            action: () => runAction(selected.id, () => approveCommunityGrading(selected.id)),
          })}
        />
      )}

      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel="Подтвердить"
          onConfirm={() => { confirmAction.action(); }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </>
  );
}
